import React, { useState } from 'react';
import { Save } from 'lucide-react';
import { IdealCustomerProfile } from '../../../types';
import { Input } from '../../ui/Input';
import { Button } from '../../ui/Button';
import { Card } from '../../ui/Card';
import { CountrySelector } from './CountrySelector';

interface IcpFormProps {
  icp: Partial<IdealCustomerProfile>;
  onSave: (icp: Partial<IdealCustomerProfile>) => void;
  onCancel: () => void;
  isSaving: boolean;
}

export const IcpForm: React.FC<IcpFormProps> = ({ icp, onSave, onCancel, isSaving }) => {
  const [formData, setFormData] = useState<Partial<IdealCustomerProfile>>({
    ...icp,
    target_region: Array.isArray(icp.target_region) ? icp.target_region : []
  });

  return (
    <Card padding="md" className="bg-white border border-neutral-200 space-y-4">
      <div>
        <label htmlFor="icp-profile-name" className="block text-[12px] font-medium text-neutral-700 mb-1.5">
          Profile Name
        </label>
        <Input
          id="icp-profile-name"
          inputSize="sm"
          fullWidth
          value={formData.name || ''}
          onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
          placeholder="e.g., Enterprise SaaS"
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label htmlFor="icp-solution-products" className="block text-[12px] font-medium text-neutral-700 mb-1.5">
            Solution & Products
          </label>
          <textarea
            id="icp-solution-products"
            value={formData.solution_products || ''}
            onChange={(e) => setFormData(prev => ({ ...prev, solution_products: e.target.value }))}
            placeholder="What are you selling?"
            rows={3}
            aria-label="Solution and products description"
            className="w-full px-3 py-2 text-[13px] rounded-xl resize-none border border-neutral-200 bg-neutral-50/50 text-neutral-900 placeholder:text-neutral-400 focus:outline-none focus:bg-white focus:border-neutral-300 focus:ring-4 focus:ring-primary-900/5 transition-all duration-200"
          />
        </div>
        <div>
          <label id="icp-target-region-label" className="block text-[12px] font-medium text-neutral-700 mb-1.5">
            Target Region
          </label>
          <CountrySelector
            selectedCountries={Array.isArray(formData.target_region) ? formData.target_region : []}
            onCountriesChange={(countries) => setFormData(prev => ({ ...prev, target_region: countries }))}
          />
        </div>
      </div>

      <div>
        <label htmlFor="icp-target-customers" className="block text-[12px] font-medium text-neutral-700 mb-1.5">
          Target Customers
        </label>
        <textarea
          id="icp-target-customers"
          value={formData.target_customers || ''}
          onChange={(e) => setFormData(prev => ({ ...prev, target_customers: e.target.value }))}
          placeholder="Describe your ideal customers..."
          rows={2}
          aria-label="Target customers description"
          className="w-full px-3 py-2 text-[13px] rounded-xl resize-none border border-neutral-200 bg-neutral-50/50 text-neutral-900 placeholder:text-neutral-400 focus:outline-none focus:bg-white focus:border-neutral-300 focus:ring-4 focus:ring-primary-900/5 transition-all duration-200"
        />
      </div>

      <div className="flex justify-end gap-2 pt-2">
        <Button id="icp-cancel-button" variant="ghost" size="sm" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          id="icp-save-button"
          variant="primary"
          size="sm"
          onClick={() => onSave(formData)}
          disabled={isSaving || !formData.name?.trim()}
          loading={isSaving}
          leftIcon={!isSaving ? <Save className="w-3 h-3" /> : undefined}
        >
          Save
        </Button>
      </div>
    </Card>
  );
};
